import axios from 'axios'
import { API_BASE } from '@/services/api'
import { getIdToken, resolveCurrentUid } from '@/utils/auth'

export type GoogleConnectionStatus = 'connected' | 'reconnect'
const TTL_MS = 60_000
const DEADLINE_MS = 15_000
type Entry = {
  value?: GoogleConnectionStatus
  checkedAt?: number
  pending?: Promise<GoogleConnectionStatus>
  abort?: () => void
}
const entries = new Map<string, Entry>()

export function invalidateGoogleConnection(uid: string) {
  const entry = entries.get(uid)
  entries.delete(uid)
  entry?.abort?.()
}

export function requireGoogleReconnect(uid: string) {
  invalidateGoogleConnection(uid)
  entries.set(uid, { value: 'reconnect', checkedAt: Date.now() })
}

export function readGoogleConnection(uid: string): Promise<GoogleConnectionStatus> {
  const previous = entries.get(uid)
  if (previous?.pending) return previous.pending
  if (previous?.value && Date.now() - (previous.checkedAt ?? 0) < TTL_MS) {
    return resolveCurrentUid().then((currentUid) => {
      if (currentUid !== uid) throw new Error('Google connection check belongs to another user')
      return previous.value!
    })
  }
  const entry: Entry = {}
  entries.set(uid, entry)
  const controller = new AbortController()
  const operation = async (): Promise<GoogleConnectionStatus> => {
    if ((await resolveCurrentUid()) !== uid || controller.signal.aborted)
      throw new Error('Check cancelled')
    const token = await getIdToken()
    if ((await resolveCurrentUid()) !== uid || controller.signal.aborted)
      throw new Error('Check cancelled')
    const { data } = await axios.get(`${API_BASE}/authApi/v1/googleConnection`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
    if (data.status !== 'connected' && data.status !== 'reconnect')
      throw new Error('Invalid connection status')
    return data.status
  }
  // The deadline includes Firebase token resolution, not just the Axios request.
  let timer: ReturnType<typeof setTimeout>
  const deadline = new Promise<never>((_, reject) => {
    entry.abort = () => {
      controller.abort()
      reject(new Error('Connection check cancelled'))
    }
    timer = setTimeout(() => {
      controller.abort()
      reject(new Error('Connection check timed out'))
    }, DEADLINE_MS)
  })
  entry.pending = Promise.race([operation(), deadline])
    .then((status) => {
      if (entries.get(uid) !== entry) throw new Error('Connection check superseded')
      entry.value = status
      entry.checkedAt = Date.now()
      return status
    })
    .finally(() => {
      clearTimeout(timer)
      entry.pending = undefined
      entry.abort = undefined
      if (!entry.value && entries.get(uid) === entry) entries.delete(uid)
    })
  return entry.pending
}
